import React, { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { MoreHorizontal, Pencil, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";

const STATUSES = ["Eingetroffen", "In Bearbeitung", "Erneut versendet", "Storniert", "Abgeschlossen"];

export default function ReturnRowActions({ ret, onChanged, onDeleted, variant = "table" }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  const changeStatus = async (status) => {
    if (status === ret.status) return;
    try {
      await api.post("/returns/bulk-status", { ids: [ret.id], status });
      toast.success(`${ret.id} → ${t(`stats.${status}`)}`);
      onChanged && onChanged({ ...ret, status });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/returns/${ret.id}`);
      toast.success(`${ret.id} gelöscht`);
      onDeleted && onDeleted(ret.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  const triggerProps = variant === "card"
    ? { variant: "ghost", size: "icon", className: "h-8 w-8 shrink-0" }
    : { variant: "outline", size: "sm", className: "h-8 w-8 p-0" };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button {...triggerProps} style={{ borderRadius: 4 }} data-testid={`return-actions-${ret.id}`} aria-label="Aktionen">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()} className="w-56">
          <DropdownMenuLabel className="text-xs font-mono">{ret.id}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate(`/returns/${ret.id}`)} data-testid={`action-open-${ret.id}`}>
            <Pencil className="w-4 h-4 mr-2" /> {t("actions.openEdit")}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid={`action-status-${ret.id}`}>
              <RefreshCw className="w-4 h-4 mr-2" /> {t("actions.changeStatus")}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {STATUSES.map((s) => (
                  <DropdownMenuItem
                    key={s}
                    onClick={() => changeStatus(s)}
                    disabled={s === ret.status}
                    data-testid={`action-status-${ret.id}-${s}`}
                  >
                    {t(`stats.${s}`)}
                    {s === ret.status && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setConfirmOpen(true)}
                className="text-destructive focus:text-destructive"
                data-testid={`action-delete-${ret.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" /> {t("actions.delete")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()} style={{ borderRadius: 4 }} data-testid={`delete-dialog-${ret.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("actions.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("actions.deleteConfirmDesc")} <span className="font-mono font-semibold">{ret.id}</span> — {ret.customer_name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderRadius: 4 }} data-testid={`delete-cancel-${ret.id}`}>
              {t("form.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              style={{ borderRadius: 4 }}
              data-testid={`delete-confirm-${ret.id}`}
            >
              {deleting ? t("common.loading") : t("actions.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
